import * as React from "react";
import {Attributes, Component, createRef} from "react";
import {OverlayTrigger, Popover} from "react-bootstrap";
import {NumericInputComponent} from "@zmags/zmags-ui-library";

import SceneTransitionTypes from "shared/ui/experience/SceneTransitionTypes";
import InputComponent from "shared/ui/common/InputComponent";
import {PropertyContainerComponent} from "./PropertyContainerComponent";
import SelectComponent, {SelectOption} from "shared/ui/common/SelectComponent";

const MIN_WIDTH = 1;
const MIN_HEIGHT =  1;
const MIN_AUTOPLAY = 0;
const ONLY_NUMBERS_REGEX = /^\d+$/;

enum BackdropType {
    Color = "Color",
    Image = "Image"
}

interface BackdropSettings {
    type: BackdropType;
    color: string;
}

interface ExperienceSettingsProps extends Attributes {
    experienceId: string;
    groupId?: string;
    spectatorMode: string;
    height: number;
    width: number;
    autoPlayDelay: number;
    autoPlayTransition: string;
    language: string;
    widgetSelected: boolean;
    backdrop: BackdropSettings;
    setExperienceHeight(height: number): void;
    setExperienceWidth(width: number): void;
    setExperienceAutoPlayDelay(delaySeconds: number): void;
    setExperienceAutoPlayTransition(transition: string): void;
    changeBackdrop(event: React.MouseEvent<HTMLElement>): void;
    setLanguage(language: string): void;
}

interface ExperienceSettingsState {
    height: number;
    width: number;
    autoPlayDelay: number;
    autoPlayTransition: string;
    language: string;
}

interface Language {
    label: string;
    value: string;
}

const languages: Language[] = [
    {label: "Danish", value: "da-DK"},
    {label: "Dutch", value: "nl-NL"},
    {label: "English (US)", value: "en-US"},
    {label: "English (CA)", value: "en-CA"},
    {label: "English (GB)", value: "en-GB"},
    {label: "Finnish", value: "fi-FI"},
    {label: "French", value: "fr-FR"},
    {label: "German", value: "de-DE"},
    {label: "Greek", value: "el-GR"},
    {label: "Italian", value: "it-IT"},
    {label: "Norwegian", value: "no-NO"},
    {label: "Polish", value: "pl-PL"},
    {label: "Portuguese", value: "pt-PT"},
    {label: "Russian", value: "ru-RU"},
    {label: "Spanish", value: "es-ES"},
    {label: "Swedish", value: "sv-SE"},
    {label: "Turkish", value: "tr-TR"},
    {label: "Ukrainian", value: "uk-UA"}
];

// noinspection JSUnusedGlobalSymbols
class ExperienceSettingsComponent extends Component<ExperienceSettingsProps, ExperienceSettingsState> {
    private experienceIdInputRef = createRef<HTMLInputElement>();
    private groupIdInputRef = createRef<HTMLInputElement>();
    constructor(props: ExperienceSettingsProps) {
        super(props);

        this.state = {
            height: props.height,
            width: props.width,
            autoPlayDelay: props.autoPlayDelay,
            autoPlayTransition: props.autoPlayTransition,
            language: props.language
        };
    }

    validateChange(newValue: number, minValue: number) {
        if (ONLY_NUMBERS_REGEX.test(String(newValue))) {
            return !(newValue < minValue || this.props.spectatorMode);
        }

        return false;
    }

    updateHeight = (newHeight: number) => {
        if (this.validateChange(newHeight, MIN_HEIGHT)) {
            this.updateModelAndState(
                this.props.setExperienceHeight,
                newHeight,
                "height"
            );
        }
    }

    updateWidth = (newWidth: number) => {
        if (this.validateChange(newWidth, MIN_WIDTH)) {
            this.updateModelAndState(
                this.props.setExperienceWidth,
                newWidth,
                "width"
            );
        }
    }

    updateAutoPlayDelay = (newAutoPlayDelay: number) => {
        if (this.validateChange(newAutoPlayDelay, MIN_AUTOPLAY)) {
            this.updateModelAndState(
                this.props.setExperienceAutoPlayDelay,
                newAutoPlayDelay,
                "autoPlayDelay"
            );
        }
    }

    updateAutoPlayTransition = (newAutoPlayTransitionOption: SelectOption) => {
        if (!this.props.spectatorMode) {
            this.updateModelAndState(
                this.props.setExperienceAutoPlayTransition,
                newAutoPlayTransitionOption.value,
                "autoPlayTransition"
            );
        }
    }

    updateLanguage = (newLanguageOption: SelectOption) => {
        const newLanguage = newLanguageOption.value;
        if (!this.props.spectatorMode) {
            this.updateModelAndState(
                this.props.setLanguage,
                newLanguage,
                "language"
            );
        }
    }

    changeBackdropClicked = (event: React.MouseEvent<HTMLElement>) => {
        this.props.changeBackdrop(event);
    }

    handleExperienceIdClick = () => {
        this.experienceIdInputRef.current.select();
        document.execCommand("copy");
    }

    handleGroupIdClick = () => {
        this.groupIdInputRef.current.select();
        document.execCommand("copy");
    }

    buildAutoPlayTransitionOptions = () => {
        return Object.keys(SceneTransitionTypes).map((key) => {
            return {value: SceneTransitionTypes[key].value.id, label: SceneTransitionTypes[key].value.label};
        });
    }

    render () {
        const {
            spectatorMode,
            widgetSelected,
            experienceId,
            groupId,
            backdrop: {
                type,
                color,
            }
        } = this.props;
        const {height, width, autoPlayDelay, autoPlayTransition, language} = this.state;
        const autoplayTransitionOptions = this.buildAutoPlayTransitionOptions();
        const inputsDisabled = !!spectatorMode;
        const isBackdropColorType = BackdropType.Color === type;
        let backdropColorClassName = "";
        if (!isBackdropColorType) {
            backdropColorClassName = "backdrop-visible";
        } else if (color === "transparent") {
            backdropColorClassName = "backdrop-transparent";
        }
        const backdropStyle = isBackdropColorType && color !== "transparent"
            ? {background: color}
            : null;

        const clickToCopyPopover = (
            <Popover id="experience-id-popover">Click to copy.</Popover>
        );

        return (
            <PropertyContainerComponent
                widgetSelected={widgetSelected}
                label={"Experience Settings"}
                name={"experience-settings"}
                animateExpandOnMount={false}
            >
                <div className="experience-settings-property">
                    <NumericInputComponent
                        label="Width:"
                        units="px"
                        classNames="experience-settings-width-input"
                        disabled={inputsDisabled}
                        onChange={this.updateWidth}
                        value={width}
                        debounceDelay={500}
                    />
                </div>
                <div className="experience-settings-property">
                    <NumericInputComponent
                        label="Height:"
                        units="px"
                        classNames="experience-settings-height-input"
                        disabled={inputsDisabled}
                        onChange={this.updateHeight}
                        value={height}
                        debounceDelay={500}
                    />
                </div>
                <div className="experience-settings-property">
                    <NumericInputComponent
                        label="Autoplay:"
                        units="sec"
                        classNames="auto-play-delay-input"
                        disabled={inputsDisabled}
                        onChange={this.updateAutoPlayDelay}
                        value={autoPlayDelay}
                    />
                </div>
                <div className="experience-settings-property">
                    <div className="sub-property">
                        <SelectComponent
                            classNames="auto-play-transition form-control"
                            disabled={inputsDisabled}
                            onChange={this.updateAutoPlayTransition}
                            value={autoplayTransitionOptions.find((option => option.value === autoPlayTransition))}
                            options={autoplayTransitionOptions}
                        />
                    </div>
                </div>
                <div className="experience-settings-property">
                    <label>
                        Backdrop:
                        <a href="#" className="show-backdrop" onClick={this.changeBackdropClicked}>
                            <span
                                className={`img-container backdrop-container ${backdropColorClassName}`}
                                style={backdropStyle}
                            />
                        </a>
                    </label>
                </div>
                <div className="experience-settings-property language-property">
                    <label>
                        Language:
                    </label>
                    <div className="selectLanguage">
                        <SelectComponent
                            classNames="language-select form-control"
                            disabled={inputsDisabled}
                            onChange={this.updateLanguage}
                            value={languages.find((lang => lang.value === language))}
                            options={languages}
                        />
                    </div>
                </div>
                <OverlayTrigger placement="bottom" overlay={clickToCopyPopover}>
                    <div className="experience-settings-property readonly-copy-property">
                        <InputComponent
                            classNames="text-box experience-copy-text-box"
                            inputElementId="experience-id-input"
                            disabled={inputsDisabled}
                            onClick={this.handleExperienceIdClick}
                            readOnly={true}
                            type="text"
                            label="ID:"
                            units={null}
                            ref={this.experienceIdInputRef}
                            value={experienceId}

                        />
                    </div>
                </OverlayTrigger>
                {groupId &&
                    <OverlayTrigger placement="bottom" overlay={clickToCopyPopover}>
                        <div className="experience-settings-property readonly-copy-property">
                            <InputComponent
                                classNames="text-box experience-copy-text-box"
                                inputElementId="experience-group-id-input"
                                disabled={inputsDisabled}
                                onClick={this.handleGroupIdClick}
                                readOnly={true}
                                type="text"
                                label="Group ID:"
                                units={null}
                                ref={this.groupIdInputRef}
                                value={groupId}
                            />
                        </div>
                    </OverlayTrigger>
                }
            </PropertyContainerComponent>
        );
    }

    private updateModelAndState(modelSetter: (value: number | string) => void,
                                value: number | string, fieldName: string) {
        modelSetter(value);
        const newState = {};
        newState[fieldName] = value;
        this.setState(newState);
    }
}

// noinspection JSUnusedGlobalSymbols
export {
    ExperienceSettingsComponent as default,
    ExperienceSettingsComponent,
};



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/toolbox/ExperienceSettingsComponent.tsx